// Status types for the status grid components

export type StatusCategory = 'CONSUMABLE' | 'EQUIPMENT' | 'CLEANLINESS' | 'COMFORT';

// Tree-provided status option (from AmenityTypeNode depth-2)
export interface TreeStatusOption {
  value: string;
  name: string;
  icon: string | null;
  severity: string | null;
  isBadStatus: boolean;
}

export interface StatusItem {
  id: string;
  statusType: {
    id: string;
    name: string;
    category: StatusCategory;
    icon: string | null;
    // Populated by the /api/status endpoint when the tenant opts into content
    // translation. When absent, components fall back to `name`.
    displayName?: string;
    nameLocale?: string;
  };
  currentStatus: string;
  lastUpdatedAt: string;
  statusOptions?: TreeStatusOption[] | null; // From tree (null = use legacy STATUS_OPTIONS)
}

// Status colors by value - aligned with TendedLoop brand
// Good = Forest (#40916C), Warning = Gold, Bad = Critical
export const STATUS_COLORS: Record<string, string> = {
  // Good statuses - Accent Green (Resolution/Positive)
  OK: 'bg-forest-wash text-forest border-forest/40',
  WORKING: 'bg-forest-wash text-forest border-forest/40',
  CLEAN: 'bg-forest-wash text-forest border-forest/40',
  COMFORTABLE: 'bg-forest-wash text-forest border-forest/40',

  // Warning statuses - Amber
  LOW: 'bg-gold/10 text-gold border-gold/40',
  NEEDS_ATTENTION: 'bg-gold/10 text-gold border-gold/40',

  // Bad statuses - Red
  EMPTY: 'bg-critical/10 text-critical border-critical/40',
  ISSUE: 'bg-critical/10 text-critical border-critical/40',
  NOT_WORKING: 'bg-critical/10 text-critical border-critical/40',
  DIRTY: 'bg-critical/10 text-critical border-critical/40',
  UNCOMFORTABLE: 'bg-critical/10 text-critical border-critical/40',
};

// Default fallback color - Primary (brand)
export const DEFAULT_STATUS_COLOR = 'bg-primary/5 text-primary border-primary/30';

// Status icons by value
export const STATUS_ICONS: Record<string, string> = {
  OK: '✓',
  WORKING: '✓',
  CLEAN: '✓',
  COMFORTABLE: '✓',
  LOW: '⚠',
  NEEDS_ATTENTION: '⚠',
  EMPTY: '✗',
  ISSUE: '✗',
  NOT_WORKING: '✗',
  DIRTY: '✗',
  UNCOMFORTABLE: '✗',
};

// Category translation keys (use with t('status.categories.xxx'))
export const CATEGORY_KEYS: Record<StatusCategory, string> = {
  CONSUMABLE: 'consumable',
  EQUIPMENT: 'equipment',
  CLEANLINESS: 'cleanliness',
  COMFORT: 'comfort',
};

// Category display order
export const CATEGORY_ORDER: StatusCategory[] = ['CONSUMABLE', 'EQUIPMENT', 'CLEANLINESS', 'COMFORT'];

// Status options by category for the update modal
// Uses translation keys: t('status.levels.xxx') and t('status.descriptions.xxx')
export interface StatusOption {
  value: string;
  labelKey: string; // Translation key for label (e.g., 'ok', 'low')
  descriptionKey: string; // Translation key for description
  icon: string;
}

export const STATUS_OPTIONS: Record<StatusCategory, StatusOption[]> = {
  CONSUMABLE: [
    { value: 'OK', labelKey: 'ok', descriptionKey: 'ok', icon: '✓' },
    { value: 'LOW', labelKey: 'low', descriptionKey: 'low', icon: '⚠' },
    { value: 'EMPTY', labelKey: 'empty', descriptionKey: 'empty', icon: '✗' },
  ],
  EQUIPMENT: [
    { value: 'WORKING', labelKey: 'working', descriptionKey: 'working', icon: '✓' },
    { value: 'ISSUE', labelKey: 'issue', descriptionKey: 'issue', icon: '⚠' },
    { value: 'NOT_WORKING', labelKey: 'notWorking', descriptionKey: 'notWorking', icon: '✗' },
  ],
  CLEANLINESS: [
    { value: 'CLEAN', labelKey: 'clean', descriptionKey: 'clean', icon: '✓' },
    { value: 'NEEDS_ATTENTION', labelKey: 'needsAttention', descriptionKey: 'needsAttention', icon: '⚠' },
    { value: 'DIRTY', labelKey: 'dirty', descriptionKey: 'dirty', icon: '✗' },
  ],
  COMFORT: [
    { value: 'COMFORTABLE', labelKey: 'comfortable', descriptionKey: 'comfortable', icon: '✓' },
    { value: 'UNCOMFORTABLE', labelKey: 'uncomfortable', descriptionKey: 'uncomfortable', icon: '✗' },
  ],
};

// Bad statuses that trigger task creation
export const BAD_STATUSES = new Set([
  'LOW',
  'EMPTY',
  'ISSUE',
  'NOT_WORKING',
  'NEEDS_ATTENTION',
  'DIRTY',
  'UNCOMFORTABLE',
]);

// Helper function to format status for display
export function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').toLowerCase();
}

// Helper to get status color class
export function getStatusColor(status: string): string {
  return STATUS_COLORS[status] || DEFAULT_STATUS_COLOR;
}

// Helper to get status icon
export function getStatusIcon(status: string): string {
  return STATUS_ICONS[status] || '?';
}

// Derive color from tree severity + isBadStatus (for tree-provided options)
export function getColorFromSeverity(severity: string | null, isBadStatus: boolean): string {
  if (!isBadStatus) return 'bg-forest-wash text-forest border-forest/40';
  if (severity === 'high') return 'bg-critical/10 text-critical border-critical/40';
  if (severity === 'medium') return 'bg-gold/10 text-gold border-gold/40';
  return 'bg-gold/10 text-gold border-gold/40'; // default bad = warning
}

// Derive icon from tree severity
export function getIconFromSeverity(severity: string | null, isBadStatus: boolean): string {
  if (!isBadStatus) return '✓';
  if (severity === 'high') return '✗';
  return '⚠';
}
